import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import rclLogo from "@/assets/rcl-logo.webp";
import mscLogo from "@/assets/msc-logo.webp";
import maerskLogo from "@/assets/maersk-logo.webp";
import cmacgmLogo from "@/assets/cmacgm-logo.webp";
import coscoLogo from "@/assets/cosco-logo.webp";
import hapagLloydLogo from "@/assets/hapag-lloyd-logo.webp";
import oneLogo from "@/assets/one-logo.webp";
import evergreenLogo from "@/assets/evergreen-logo.webp";
import hmmLogo from "@/assets/hmm-logo.webp";
import yangmingLogo from "@/assets/yangming-logo.webp";
import smartMaritimeLogo from "@/assets/smart-maritime-logo.webp";
import chinaEasternLogo from "@/assets/china-eastern-logo.webp";
import chinaSouthernLogo from "@/assets/china-southern-logo.webp";

type ShapeType = "circle" | "square" | "triangle" | "diamond" | "hexagon" | "pentagon";

interface Partner {
  name: string;
  shape: ShapeType;
  logo?: string;
}

const partners: Partner[] = [
  { name: "MSC", shape: "circle", logo: mscLogo },
  { name: "Maersk", shape: "square", logo: maerskLogo },
  { name: "CMA CGM", shape: "triangle", logo: cmacgmLogo },
  { name: "COSCO", shape: "hexagon", logo: coscoLogo },
  { name: "Hapag-Lloyd", shape: "pentagon", logo: hapagLloydLogo },
  { name: "ONE", shape: "circle", logo: oneLogo },
  { name: "Evergreen", shape: "diamond", logo: evergreenLogo },
  { name: "HMM", shape: "hexagon", logo: hmmLogo },
  { name: "Yang Ming", shape: "square", logo: yangmingLogo },
  { name: "Smart Maritime Co., Ltd", shape: "triangle", logo: smartMaritimeLogo },
  { name: "Regional Container Lines (RCL)", shape: "diamond", logo: rclLogo },
  { name: "China Eastern", shape: "circle", logo: chinaEasternLogo },
  { name: "China Southern", shape: "circle", logo: chinaSouthernLogo },
];

const partnersReversed = [...partners].reverse();

// 浅色主色绿 - 用于文字和矩形背景
const PRIMARY_GREEN_LIGHT = "hsl(170, 55%, 75%)";
// 深色主色绿 - 用于实心几何图标
const PRIMARY_GREEN_DARK = "hsl(170, 55%, 25%)";

function GeometricLogo({ shape }: { shape: ShapeType }) {
  const renderShape = () => {
    switch (shape) {
      case "circle":
        return <circle cx="14" cy="14" r="6" fill={PRIMARY_GREEN_DARK} />;
      case "square":
        return <rect x="9" y="9" width="10" height="10" rx="1" fill={PRIMARY_GREEN_DARK} />;
      case "triangle":
        return <polygon points="14,7 21,19 7,19" fill={PRIMARY_GREEN_DARK} />;
      case "diamond":
        return <polygon points="14,6 20,14 14,22 8,14" fill={PRIMARY_GREEN_DARK} />;
      case "hexagon":
        return <polygon points="14,5 21,9 21,17 14,21 7,17 7,9" fill={PRIMARY_GREEN_DARK} />;
      case "pentagon":
        return <polygon points="14,5 21,11 18,20 10,20 7,11" fill={PRIMARY_GREEN_DARK} />;
      default:
        return <circle cx="14" cy="14" r="6" fill={PRIMARY_GREEN_DARK} />;
    }
  };

  return (
    <svg width="28" height="28" viewBox="0 0 28 28" className="flex-shrink-0">
      {/* 浅色矩形背景 */}
      <rect x="1" y="1" width="26" height="26" rx="4" fill={PRIMARY_GREEN_LIGHT} />
      {/* 实心深色几何图形 */}
      {renderShape()}
    </svg>
  );
}

function useCountUp(end: number, duration: number = 2000, startCounting: boolean = false) {
  // Initialize with the final value so SSR/pre-hydration HTML shows the real number.
  const [count, setCount] = useState(end);

  useEffect(() => {
    if (!startCounting) return;

    // Reset to 0 once the client begins the visible animation, then count up.
    setCount(0);

    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, startCounting]);

  return count;
}

function CountUpStat({ value, label, suffix = "" }: { value: number; label: string; suffix?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const count = useCountUp(value, 2000, isInView);
  
  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl font-extrabold text-gradient-ai">{count}{suffix}</div>
      <div className="text-base md:text-lg text-muted-foreground mt-2">{label}</div>
    </div>
  );
}

export function PartnersSection() {
  const { t } = useLanguage();
  const doubledPartners = [...partners, ...partners];
  const doubledPartnersReversed = [...partnersReversed, ...partnersReversed];

  return (
    <section className="py-12 bg-muted/30 overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/10 text-secondary text-sm font-medium mb-4">
            {t("partners.badge")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t("partners.title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("partners.desc")}
          </p>
        </motion.div>

        <div className="relative mb-6">
          
          <motion.div
            className="flex gap-12 whitespace-nowrap"
            animate={{ x: [0, -50 * partners.length] }}
            transition={{ x: { repeat: Infinity, repeatType: "loop", duration: 30, ease: "linear" } }}
          >
            {doubledPartners.map((partner, index) => (
              <span key={`row1-${index}`} className="text-xl md:text-2xl font-semibold transition-colors duration-300 cursor-default flex items-center gap-3 text-muted-foreground/70">
                {partner.logo ? (
                  <img src={partner.logo} alt={partner.name} loading="lazy" decoding="async" className="h-8 md:h-10 w-auto max-w-[120px] object-contain flex-shrink-0" />
                ) : (
                  <GeometricLogo shape={partner.shape} />
                )}
                <span>{partner.name}</span>
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative">
          
          <motion.div
            className="flex gap-12 whitespace-nowrap"
            animate={{ x: [-50 * partners.length, 0] }}
            transition={{ x: { repeat: Infinity, repeatType: "loop", duration: 35, ease: "linear" } }}
          >
            {doubledPartnersReversed.map((partner, index) => (
              <span key={`row2-${index}`} className="text-xl md:text-2xl font-semibold transition-colors duration-300 cursor-default flex items-center gap-3 text-muted-foreground/70">
                {partner.logo ? (
                  <img src={partner.logo} alt={partner.name} loading="lazy" decoding="async" className="h-8 md:h-10 w-auto max-w-[120px] object-contain flex-shrink-0" />
                ) : (
                  <GeometricLogo shape={partner.shape} />
                )}
                <span>{partner.name}</span>
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-8 mt-16 pt-12 border-t border-border"
        >
          <CountUpStat value={50} suffix="+" label={t("partners.shipping")} />
          <CountUpStat value={100} suffix="+" label={t("partners.air")} />
          <CountUpStat value={200} suffix="+" label={t("partners.ports")} />
          <CountUpStat value={150} suffix="+" label={t("partners.countries")} />
        </motion.div>
      </div>
    </section>
  );
}
